import { Alert, Button, Drawer, Flex, Form } from 'antd'
import React, { useState } from 'react'
import { AntdForm } from '@/common/components/AntdForm'
import { Checklist } from '@/common/types/checklist.def.ts'
import { renderChecklistCustomField } from '@/pages/checklist/components/NewChecklistForm.tsx'
import { ChecklistCustomField, ChecklistCustomFieldMapping } from '@/features/checklist/checklist.def.ts'
import { handleApiError } from '@/utils/api.tsx'
import { useEditChecklistMutation } from '@/features/checklist/checklist.api.ts'
import { useAuth } from '@/common/hooks/useAuth.ts'

type EditChecklistFormProps = {
  checklist: Checklist
}

export type EditChecklistFormValues = {
  lineManagerEmployeeId?: string
  joinDate?: string
  verbalOfferAcceptanceDate?: string
  noticeDate?: string
  terminationDate?: string
  resignationDate?: string
  lastWorkingDay?: string
}

const getEditableFields = (checklist: Checklist) => {
  return checklist.extraDetails.filter(x => {
    const field = ChecklistCustomFieldMapping[x.fieldName]

    if (!field) return false

    return field.type === 'date' || x.fieldName === ChecklistCustomField.LineManagerEmployeeId
  })
}

export const EditChecklistForm: React.FC<EditChecklistFormProps> = ({ checklist }) => {
  const { checkHasAllPermissions } = useAuth()
  const [open, setOpen] = useState(false)
  const [editChecklist, { isLoading }] = useEditChecklistMutation()
  const [form] = Form.useForm()
  const hasAccess = checkHasAllPermissions([{
    moduleName: 'human-capital-portal',
    functionName: 'read-write-checklist',
  }])
  const allItemsHasRefDateType = checklist.checklistGroupItems.every(gp => gp.items.every(x => !!x.refDateType))
  const canEdit = hasAccess && !checklist.isComplete && allItemsHasRefDateType

  const editableFields = getEditableFields(checklist)

  const handleOpen = () => {
    const values = editableFields.reduce((acc, item) => {
      const field = ChecklistCustomFieldMapping[item.fieldName]
      return {
        [field.name]: item.value,
        ...acc,
      }
    }, {})
    form.setFieldsValue(values)
    setOpen(true)
  }

  const handleClose = () => {
    form.resetFields()
    setOpen(false)
  }

  const handleFinish = async (values: EditChecklistFormValues) => {
    try {
      await editChecklist({
        ...values,
        checklistId: checklist.id,
      }).unwrap()
    } catch (e) {
      handleApiError(e)
    }
  }

  if (!hasAccess) return null

  return (
    <>
      <Button type="primary" onClick={handleOpen} disabled={!canEdit}>Edit Basic Info</Button>
      <Drawer open={open} onClose={handleClose} destroyOnHidden title="Edit Checklist Basic Info" width={800}>
        <Alert
          showIcon type="info"
          message="Edit basic info will trigger re-assignment of assignee, update deadline"
        />
        <Form layout="vertical" form={form} onFinish={handleFinish}>
          <AntdForm.Section>
            {editableFields.map(x => renderChecklistCustomField(x.fieldName))}
          </AntdForm.Section>
          <Flex justify="end">
            <Button type="primary" htmlType="submit" loading={isLoading}>Save</Button>
          </Flex>
        </Form>
      </Drawer>
    </>
  )
}